import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { CheckIcon } from '../../components/public/icons';
import { mainSiteUrl } from '../../constants/discovery';

const SPECIALTY_OPTIONS = [
  'Nail tech',
  'Hairstylist / braider',
  'Lash tech',
  'Makeup artist',
  'Esthetician',
  'Other beauty professional',
];
const TRAVEL_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'sometimes', label: 'Sometimes' },
];
const INTEREST_OPTIONS = [
  { value: 'discovery', label: 'Discovery' },
  { value: 'booking', label: 'Booking' },
  { value: 'both', label: 'Both' },
];
const EMPTY_FORM = {
  name: '',
  businessName: '',
  email: '',
  instagram: '',
  specialty: '',
  city: '',
  state: '',
  travelsToClients: '',
  interest: 'discovery',
  emailConsent: false,
};
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fieldValidity = (form) => ({
  name: Boolean(form.name.trim()),
  email: EMAIL_PATTERN.test(form.email.trim()),
  specialty: Boolean(form.specialty),
  city: Boolean(form.city.trim()),
  state: Boolean(form.state.trim()),
});

const validate = (form) => {
  const valid = fieldValidity(form);
  if (!valid.name || !valid.specialty || !valid.city || !valid.state) return 'Please add your name, specialty, city, and state.';
  if (!valid.email) return 'Please enter a valid email address.';
  if (!form.travelsToClients) return 'Let us know if you travel to clients.';
  if (!form.emailConsent) return 'Please agree to receive Discovery emails so we can reach you.';
  return '';
};

const ChoiceGroup = ({ id, label, options, value, onChange }) => (
  <div className="discovery-join__group">
    <span id={id} className="discovery-join__group-label">{label}</span>
    <div className="discovery-join__choices" role="group" aria-labelledby={id}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="discovery-join__choice"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
);

const DiscoveryJoinForm = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [tried, setTried] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(null);
  const joinedHeadingRef = useRef(null);

  useEffect(() => {
    if (joined) joinedHeadingRef.current?.focus();
  }, [joined]);

  const set = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const onInput = (field) => (event) => set(field, event.target.value);
  const valid = fieldValidity(form);
  const invalid = (field) => tried && !valid[field];

  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    const problem = validate(form);
    setTried(true);
    setError(problem);
    if (problem) return;

    setSubmitting(true);
    try {
      await axios.post('/api/discovery/waitlist', {
        ...form,
        name: form.name.trim(),
        businessName: form.businessName.trim(),
        email: form.email.trim(),
        instagram: form.instagram.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
      });
      setJoined({
        firstName: form.name.trim().split(/\s+/)[0],
        email: form.email.trim(),
        location: [form.city.trim(), form.state.trim()].join(', '),
      });
    } catch (requestError) {
      setError(requestError.response?.data?.message
        || 'We couldn’t add you just now. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(EMPTY_FORM);
    setTried(false);
    setError('');
    setJoined(null);
  };

  if (joined) {
    return (
      <div className="discovery-join__card discovery-join__done">
        <span className="discovery-join__done-icon"><CheckIcon size={26} /></span>
        <h3 ref={joinedHeadingRef} tabIndex={-1}>You&rsquo;re on the list, {joined.firstName}.</h3>
        <p>We&rsquo;ll email {joined.email} as Discovery takes shape in {joined.location}.</p>
        <span className="discovery-hand discovery-join__done-note">see you in the community</span>
        <button type="button" className="discovery-btn discovery-btn--outline" onClick={reset}>
          Add another professional
        </button>
      </div>
    );
  }

  return (
    <form className="discovery-join__card discovery-join__form" onSubmit={submit} noValidate>
      <div>
        <h3>Join the founding community</h3>
        <p className="discovery-join__intro">A few details to get to know your business.</p>
      </div>
      <div className="discovery-join__fields">
        <label>
          Your name
          <input value={form.name} onChange={onInput('name')} autoComplete="name" maxLength={100} aria-invalid={invalid('name')} />
        </label>
        <label>
          Business name (optional)
          <input value={form.businessName} onChange={onInput('businessName')} autoComplete="organization" maxLength={120} />
        </label>
        <label>
          Email
          <input type="email" value={form.email} onChange={onInput('email')} autoComplete="email" maxLength={254} aria-invalid={invalid('email')} />
        </label>
        <label>
          Instagram (optional)
          <input value={form.instagram} onChange={onInput('instagram')} placeholder="@yourhandle" maxLength={60} />
        </label>
        <label>
          Specialty
          <select value={form.specialty} onChange={onInput('specialty')} aria-invalid={invalid('specialty')}>
            <option value="">Select a specialty</option>
            {SPECIALTY_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </label>
        <label>
          City
          <input value={form.city} onChange={onInput('city')} autoComplete="address-level2" placeholder="e.g. Austin" maxLength={100} aria-invalid={invalid('city')} />
        </label>
        <label>
          State
          <input value={form.state} onChange={onInput('state')} autoComplete="address-level1" placeholder="e.g. TX" maxLength={60} aria-invalid={invalid('state')} />
        </label>
      </div>
      <ChoiceGroup
        id="discovery-join-travel"
        label="Do you travel to clients?"
        options={TRAVEL_OPTIONS}
        value={form.travelsToClients}
        onChange={(value) => set('travelsToClients', value)}
      />
      <ChoiceGroup
        id="discovery-join-interest"
        label="I'm interested in"
        options={INTEREST_OPTIONS}
        value={form.interest}
        onChange={(value) => set('interest', value)}
      />
      <label className="discovery-join__consent">
        <input type="checkbox" checked={form.emailConsent} onChange={(event) => set('emailConsent', event.target.checked)} />
        <span>
          I agree to receive Haastia emails about Discovery and my selected interests. Unsubscribe anytime
          at <a href="mailto:info@haastia.com">info@haastia.com</a>.
        </span>
      </label>
      {error && <p role="alert" className="discovery-join__error">{error}</p>}
      <button type="submit" className="discovery-btn discovery-btn--primary discovery-join__submit" disabled={submitting}>
        {submitting ? 'Joining…' : <>Join the founding community <span aria-hidden="true">&rarr;</span></>}
      </button>
      <p className="discovery-join__fineprint">
        Your details are used to manage this list and contact you about Haastia.{' '}
        <a href={mainSiteUrl('/privacy')}>Privacy policy</a>
      </p>
    </form>
  );
};

export default DiscoveryJoinForm;
